<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import { gsap } from "gsap";
import Expedia from "@components/logos/Expedia.vue";
import Speed from "@components/logos/Speed.vue";
import IconX from "@components/icons/X.vue";
import SpeedText from "@components/logos/SpeedText.vue";
import Button from "@components/Button.vue";
import MarkdownPage from "@components/MarkdownPage.vue";
import { useGlobalStore } from "@stores/global";
import { LAUNCH_AT } from "@utils/launch";

const globalStore = useGlobalStore();

const showAbout = ref(false);

const now = ref(Date.now());
const dotCount = ref(1);
let timer;
let dotsInterval;
onMounted(async () => {
    timer = setInterval(() => {
        now.value = Date.now();
        if (globalStore.countdown && now.value >= LAUNCH_AT) {
            globalStore.setCountdownDone();
        }
    }, 1000);
    dotsInterval = setInterval(() => {
        dotCount.value = (dotCount.value % 3) + 1;
    }, 400);
    await nextTick();
    playReveal();
});
onUnmounted(() => {
    clearInterval(timer);
    clearInterval(dotsInterval);
});

const rootEl = ref(null);

function playReveal() {
    if (!rootEl.value) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const targets = rootEl.value.querySelectorAll("[data-reveal]");
    if (!targets.length) return;

    gsap.killTweensOf(targets);
    gsap.fromTo(
        targets,
        { opacity: 0, y: 24 },
        {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power2.out",
            stagger: 0.1,
        },
    );
}

watch(
    () => globalStore.countdown,
    async () => {
        await nextTick();
        playReveal();
    },
);

const units = computed(() => {
    const diff = Math.max(0, LAUNCH_AT - now.value);
    const s = Math.floor(diff / 1000);
    return [
        { label: "Days", value: Math.floor(s / 86400) },
        { label: "Hrs", value: Math.floor((s % 86400) / 3600) },
        { label: "Min", value: Math.floor((s % 3600) / 60) },
        { label: "Sec", value: s % 60 },
    ];
});

const digits = (n) =>
    String(Math.min(99, n)).padStart(2, "0").split("").map(Number);

function trackYouTubeClick() {
    if (typeof window.gtag === "function") {
        window.gtag("event", "countdown_clicked_youtube", {
            event_label: "Countdown - Clicked YouTube",
        });
    }
}
</script>

<template>
    <div ref="rootEl" class="fixed inset-0 isolate z-10">
        <Transition name="landing-fade" mode="out-in">
            <div v-if="globalStore.countdown" class="landing-grid">
                <div
                    data-reveal
                    class="landing-grid__top flex flex-col items-center justify-center gap-10 text-center"
                >
                    <div class="flex items-center justify-center gap-4">
                        <Expedia class="h-6 w-auto" />
                        <IconX class="size-5 text-yellow-400" />
                        <Speed class="h-6 w-auto" />
                    </div>

                    <h1
                        class="leading-[0.9] font-extrabold tracking-[-0.11em] uppercase lg:tracking-[-0.22em]"
                    >
                        <span class="display-md font-normal"
                            >Go places like</span
                        ><br />
                        <SpeedText class="mt-2 h-auto w-full text-yellow-400" />
                    </h1>
                </div>

                <div
                    data-reveal
                    class="landing-grid__infos bg-marine-800 border-marine-500 flex flex-col items-center justify-center gap-6 border-r border-b p-4 md:gap-10 md:p-10"
                >
                    <div
                        class="flex w-full items-start justify-center gap-2 text-center lg:justify-between lg:gap-0"
                    >
                        <div
                            v-for="unit in units"
                            :key="unit.label"
                            class="flex flex-col items-center gap-2 md:gap-4"
                        >
                            <div
                                class="bg-marine-600 relative flex items-center justify-center rounded-lg px-3 py-4 sm:px-4"
                            >
                                <div
                                    v-for="(d, i) in digits(unit.value)"
                                    :key="i"
                                    class="sm:display-lg display-sm firefox:pt-[0.15em] inline-block h-[0.9em] overflow-hidden leading-[0.9] font-extrabold text-yellow-400 uppercase"
                                >
                                    <div
                                        class="flex flex-col transition-transform duration-600 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                                        :style="{
                                            transform: `translateY(-${d * 10}%)`,
                                        }"
                                    >
                                        <div
                                            class="flex h-[0.9em] shrink-0 items-center justify-center"
                                            v-for="n in 10"
                                            :key="n"
                                        >
                                            {{ n - 1 }}
                                        </div>
                                    </div>
                                </div>
                                <span
                                    class="bg-marine-800 absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2"
                                />
                                <span
                                    class="bg-marine-400 absolute top-1/2 left-[3px] h-4 w-[3px] -translate-y-1/2 rounded-full"
                                />
                                <span
                                    class="bg-marine-400 absolute top-1/2 right-[3px] h-4 w-[3px] -translate-y-1/2 rounded-full"
                                />
                            </div>
                            <span
                                class="body-md font-medium faded-text uppercase"
                            >
                                {{ unit.label }}
                            </span>
                        </div>
                    </div>

                    <div
                        class="flex w-full items-center justify-center gap-4 md:gap-10"
                    >
                        <div>
                            <a
                                href="https://www.youtube.com/@ishowspeed"
                                target="_blank"
                                rel="noopener"
                                class="flex h-10 items-stretch md:h-16"
                                @click="trackYouTubeClick"
                            >
                                <div
                                    class="bg-marine-600 border-marine-500 flex aspect-square shrink-0 items-center justify-center border-r"
                                >
                                    <svg
                                        class="size-4 md:size-6"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"
                                            fill="#ff0000"
                                        />
                                        <path
                                            d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                                            fill="#fff"
                                        />
                                    </svg>
                                </div>
                                <div
                                    class="bg-marine-600 flex items-center justify-center px-3 md:px-6"
                                >
                                    <span
                                        class="body-lg leading-none font-extrabold tracking-[0.2px] uppercase"
                                    >
                                        @Ishowspeed
                                    </span>
                                </div>
                            </a>
                        </div>

                        <div class="flex flex-col gap-1 leading-[0.9]">
                            <p
                                class="body-lg font-bold tracking-[-0.02em] uppercase"
                            >
                                Watch live
                            </p>
                            <p
                                class="body-lg font-bold tracking-[-0.02em] text-yellow-400 uppercase"
                            >
                                4.29.26 7:40AM ET
                            </p>
                        </div>
                    </div>

                    <div
                        class="body-xs flex w-full flex-col items-center gap-3 text-center leading-tight"
                    >
                        <p>
                            &copy; {{ new Date().getFullYear() }} Expedia Group
                            &amp; iShowSpeed. All rights reserved.
                        </p>
                        <div class="flex flex-wrap justify-center gap-3">
                            <!-- <button
                            type="button"
                            class="uppercase underline faded-text transition-opacity hover:opacity-100"
                            @click="showAbout = true"
                        >
                            About
                        </button> -->
                            <a
                                href="https://www.expedia.com/lp/b/terms-of-service"
                                target="_blank"
                                class="uppercase underline faded-text transition-opacity hover:opacity-100"
                            >
                                Terms and Conditions
                            </a>
                            <a
                                href="https://www.expedia.com/legal/privacy"
                                target="_blank"
                                class="uppercase underline faded-text transition-opacity hover:opacity-100"
                            >
                                Privacy Policy
                            </a>
                            <button
                                id="ot-sdk-btn"
                                class="ot-sdk-show-settings uppercase underline faded-text transition-opacity hover:opacity-100"
                            >
                                Cookie Settings
                            </button>
                            <a
                                href="mailto:ishowspeed@expediagroup.com"
                                class="uppercase underline faded-text transition-opacity hover:opacity-100"
                            >
                                Contact Us
                            </a>
                        </div>
                    </div>
                </div>

                <Transition name="panel" appear>
                    <MarkdownPage
                        v-if="showAbout"
                        title="About"
                        slug="about"
                        :close="() => (showAbout = false)"
                    />
                </Transition>
            </div>

            <div
                v-else
                class="flex h-svh flex-col items-center justify-center gap-12 text-center"
            >
                <h1
                    data-reveal
                    class="leading-[0.9] font-extrabold tracking-[-0.11em] uppercase lg:tracking-[-0.22em]"
                >
                    <span class="display-md font-normal">Go places like</span
                    ><br />
                    <SpeedText class="mt-2 h-auto w-full text-yellow-400" />
                </h1>

                <div data-reveal class="flex items-center justify-center gap-4">
                    <Expedia class="h-6 w-auto" />
                    <IconX class="size-5 text-yellow-400" />
                    <Speed class="h-6 w-auto" />
                </div>

                <Button
                    data-reveal
                    class="min-w-40"
                    :disabled="!globalStore.globeReady"
                    @click="globalStore.startPlaying()"
                >
                    <Transition name="fade" mode="out-in">
                        <span v-if="globalStore.globeReady" key="start">
                            Start now
                        </span>
                        <span v-else key="loading" class="loading-dots">
                            Loading<span
                                class="transition-opacity duration-200"
                                :class="{ 'opacity-0': dotCount < 1 }"
                                >.</span
                            ><span
                                class="transition-opacity duration-200"
                                :class="{ 'opacity-0': dotCount < 2 }"
                                >.</span
                            ><span
                                class="transition-opacity duration-200"
                                :class="{ 'opacity-0': dotCount < 3 }"
                                >.</span
                            >
                        </span>
                    </Transition>
                </Button>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.landing-grid {
    display: grid;
    width: 100%;
    height: 100svh;
    grid-template-columns: repeat(var(--cols), var(--cell-x));
    grid-template-rows: repeat(var(--rows), var(--cell-y));
}

.landing-grid__top {
    grid-column: 1 / -1;
    grid-row: 1 / calc(var(--rows) - 5);
}

.landing-grid__infos {
    grid-column: calc(round(down, (var(--cols) - 12) / 2, 1) + 1) / span 12;
    grid-row: -7 / -1;
}

@media (max-width: 1024px) {
    .landing-grid__top {
        grid-column: 1 / -1;
        grid-row: 1 / calc(var(--rows) - 6);
    }

    .landing-grid__infos {
        grid-column: 1 / -1;
        grid-row: -7 / -1;
    }
}

@media (min-width: 1921px) and (min-height: 1001px) {
    .landing-grid {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2.5rem;
    }

    .landing-grid__top,
    .landing-grid__infos {
        grid-column: unset;
        grid-row: unset;
        flex-shrink: 0;
    }

    .landing-grid__infos {
        width: calc(12 * var(--cell-x));
        max-width: 100%;
    }
}

@media (max-height: 600px) {
    .landing-grid {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2.5rem;
        padding: 2rem 1rem;
        height: 100svh;
        overflow-y: auto;
    }

    .landing-grid__top,
    .landing-grid__infos {
        grid-column: unset;
        grid-row: unset;
        flex-shrink: 0;
    }

    .landing-grid__infos {
        width: calc(12 * var(--cell-x));
        max-width: 100%;
    }
}

:deep(#ot-sdk-btn.ot-sdk-show-settings) {
    background: transparent;
    color: inherit;
    height: auto;
    white-space: inherit;
    word-wrap: inherit;
    padding: 0;
    font-size: inherit;
    line-height: inherit;
}

:deep(.legal-panel) {
    grid-row: 1 / -1;
}

.landing-fade-enter-active,
.landing-fade-leave-active {
    transition: opacity 600ms ease;
}

.landing-fade-enter-from,
.landing-fade-leave-to {
    opacity: 0;
}
</style>
